import {
  type Client,
  type ClientProviderEvents,
  type EvaluationContext,
  OpenFeature,
} from '@openfeature/web-sdk';
import { AmplitudeBrowserProvider } from '@wmg-common/ff-web';

import { env } from '~/lib/env.ts';

interface InitOpenFeatureFlagConnectorOptions {
  accessKey: string;
  domain: string;
  interval?: number;
  proxyUrl?: string;
}

class OpenFeatureFlagConnector {
  private initialized = false;
  private timeout: number | undefined = undefined;
  private readonly accessKey: string;
  private readonly domain: string;
  private readonly interval: number;
  private readonly proxyUrl?: string;

  constructor({
    accessKey,
    domain,
    interval = 30,
    proxyUrl,
  }: InitOpenFeatureFlagConnectorOptions) {
    this.accessKey = accessKey;
    this.domain = domain;
    this.interval = interval * 1000;
    this.proxyUrl = proxyUrl;
  }

  async init(context?: EvaluationContext) {
    if (this.initialized) {
      return;
    }

    await OpenFeature.setProviderAndWait(
      this.domain,
      new AmplitudeBrowserProvider(
        this.accessKey,
        {
          serverUrl: origin.includes('localhost') ? this.proxyUrl : origin,
        },
        { withAnalytics: true },
      ),
      context!,
    );

    await this.updateContext();
    this.initialized = true;
    // Commenting this out due to Co-Op not handling rerendering correctly.
    // this.updateContextPeriodically();
  }

  private updateContextPeriodically() {
    window.clearTimeout(this.timeout);

    this.timeout = window.setTimeout(() => {
      this.updateContext().then(() => this.updateContextPeriodically());
    }, this.interval);
  }

  async updateContext() {
    return await this.setContext(this.getContext());
  }

  async setContext(context: EvaluationContext) {
    await OpenFeature.setContext(this.domain, context);
  }

  getContext() {
    return OpenFeature.getContext(this.domain);
  }

  getClient(): Client {
    return OpenFeature.getClient(this.domain);
  }

  addHandler(eventType: ClientProviderEvents, handler: () => void) {
    OpenFeature.addHandler(eventType, handler);
  }

  removeHandler(eventType: ClientProviderEvents, handler: () => void) {
    OpenFeature.removeHandler(eventType, handler);
  }
}

export const openFeatureFlag = new OpenFeatureFlagConnector({
  accessKey: env('VITE_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY', false),
  domain: 'container',
  proxyUrl: env('VITE_AMPLITUDE_EXPERIMENT_PROXY_URL', false) || undefined,
});
